import React, { useState } from "react";
import { Modal, Button, notification, Spin, Upload, Popconfirm } from "antd";
import {
  DownloadOutlined,
  FileOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import moment from "moment";
import {
  IconTabPreSvg,
  IconTabProcedureSvg,
} from "../../../../../helpers/icons";
import { proceduresService } from "../../../../../services";
import { styles } from "./styles";

// ✅ ENUM PARA OS ESTÁGIOS
const ATTACHMENT_STAGES = {
  PRE: { key: "PRE", value: 1, label: "PRÉ-ANESTÉSICO" },
  TRANS: { key: "TRANS", value: 2, label: "TRANSOPERATÓRIO" },
  POS: { key: "POS", value: 3, label: "PÓS-ANESTÉSICO" },
};

const download = (attachmentUuid, filename) => {
  return proceduresService.downloadAttachment(attachmentUuid, filename);
};

const AttachmentsModal = ({
  visible,
  onClose,
  attachments,
  procedureId,
  onRefresh,
}) => {
  return (
    <Modal
      title="Anexos do procedimento"
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Fechar
        </Button>,
      ]}
      width="40%"
      bodyStyle={styles.modalContainer}
      centered
    >
      <AttachmentsSection
        section={ATTACHMENT_STAGES.PRE.label}
        icon={IconTabPreSvg}
        attachments={attachments.filter(
          (attachment) => attachment.stage === ATTACHMENT_STAGES.PRE.key
        )}
        stage={ATTACHMENT_STAGES.PRE.key}
        procedureId={procedureId}
        onRefresh={onRefresh}
      />
      <AttachmentsSection
        section={ATTACHMENT_STAGES.TRANS.label}
        icon={IconTabProcedureSvg}
        attachments={attachments.filter(
          (attachment) => attachment.stage === ATTACHMENT_STAGES.TRANS.key
        )}
        stage={ATTACHMENT_STAGES.TRANS.key}
        procedureId={procedureId}
        onRefresh={onRefresh}
      />
      <AttachmentsSection
        section={ATTACHMENT_STAGES.POS.label}
        icon={IconTabPreSvg}
        attachments={attachments.filter(
          (attachment) => attachment.stage === ATTACHMENT_STAGES.POS.key
        )}
        stage={ATTACHMENT_STAGES.POS.key}
        procedureId={procedureId}
        onRefresh={onRefresh}
      />
    </Modal>
  );
};

const AttachmentsSection = ({
  section,
  icon,
  attachments,
  stage,
  procedureId,
  onRefresh,
}) => {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (file, stage, onSuccess, onError) => {
    console.log({ procedureId, attachments });

    const getProcedureId = function() {
      if (procedureId) {
        return procedureId;
      }

      return attachments.id;
    };

    if (!getProcedureId()) {
      notification.error({
        message: "Erro: ID do procedimento não encontrado",
      });
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);

      // ✅ USAR ENUM EM VEZ DE SWITCH
      const stageConfig = ATTACHMENT_STAGES[stage];
      const stageNumber = stageConfig
        ? stageConfig.value
        : ATTACHMENT_STAGES.PRE.value;

      formData.append("stage", stageNumber.toString());

      const response = await proceduresService.uploadAttachment(
        getProcedureId(),
        formData
      );

      notification.success({
        message: "Anexo adicionado com sucesso!",
      });

      if (onSuccess) onSuccess();
      if (onRefresh) onRefresh();
    } catch (error) {
      notification.error({
        message: "Erro ao adicionar anexo",
        description:
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          error.message ||
          "Tente novamente mais tarde.",
      });

      if (onError) onError(error);
    } finally {
      setUploading(false);
    }
  };

  const uploadProps = {
    name: "file",
    multiple: false,
    showUploadList: false,
    customRequest: ({ file, onSuccess, onError }) => {
      handleUpload(file, stage, onSuccess, onError);
    },
  };

  return (
    <div style={styles.attachmentsContainer}>
      <div
        style={{
          ...styles.fileIconContainer,
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: "30px",
        }}
      >
        <span style={styles.title}>
          <IconTabPreSvg style={{ marginRight: 8 }} />
          {section}
        </span>

        <Upload {...uploadProps}>
          <Button
            type="link"
            icon={<PlusOutlined />}
            loading={uploading}
            style={{
              color: "#1BBFBB",
              fontSize: "14px",
              padding: "8px 16px",
              minWidth: "100px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {uploading ? "Enviando..." : "Adicionar"}
          </Button>
        </Upload>
      </div>
      {attachments.length ? (
        <AttachmentsList
          attachments={attachments}
          procedureId={procedureId}
          onRefresh={onRefresh}
        />
      ) : (
        <NoAttachments />
      )}
    </div>
  );
};

const AttachmentItemList = ({ attachment, procedureId, onRefresh }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDownload = async (attachmentUuid, filename) => {
    if (isDownloading) return;
    setIsDownloading(true);

    download(attachmentUuid, filename)
      .catch((e) => {
        notification.error({
          message:
            "OPS! Falha ao realizar download, tente novamente mais tarde!",
        });
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  const handleDelete = async () => {
    const attachmentUuid = attachment.uuid;

    if (!attachmentUuid) {
      notification.error({
        message: "Erro: UUID do anexo não encontrado",
        description: "Não foi possível identificar o anexo para exclusão.",
      });
      return;
    }

    setIsDeleting(true);

    try {
      const response = await proceduresService.deleteProcedureAttachment(
        attachmentUuid
      );

      notification.success({
        message: "Anexo excluído com sucesso!",
      });

      onRefresh();
    } catch (error) {
      notification.error({
        message: "Erro ao excluir anexo",
        description:
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          error.message ||
          "Tente novamente mais tarde.",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div style={styles.listItemContainer}>
      <div style={styles.verticalAlignCenter}>
        <div style={{ ...styles.icon, ...styles.marginRight }}>
          <FileOutlined />
        </div>
        <div style={styles.flexColumn}>
          <h3 style={styles.text}>
            {attachment.filename + attachment.file_extension}
          </h3>
          <p style={styles.text}>Descrição:{attachment.description}</p>
          <p style={styles.text}>
            Criado em:
            {moment(attachment.created_at).format("DD/MM/YYYY HH:mm")}
          </p>
        </div>
      </div>

      <div style={{ ...styles.marginRight, display: "flex", gap: "20px" }}>
        <Button
          type="link"
          style={styles.icon}
          onClick={() =>
            handleDownload(
              attachment.uuid,
              attachment.filename + attachment.file_extension
            )
          }
          disabled={isDownloading || isDeleting}
        >
          {isDownloading ? (
            <Spin spinning size="small" />
          ) : (
            <DownloadOutlined />
          )}
        </Button>

        {isDeleting ? (
          <Button
            type="link"
            style={{ ...styles.icon, color: "#ccc" }}
            disabled
          >
            <Spin spinning size="small" />
          </Button>
        ) : (
          <Popconfirm
            title="Tem certeza que deseja excluir este anexo?"
            onConfirm={handleDelete}
            okText="Sim, excluir"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="link"
              style={{ ...styles.icon, color: "#ff4d4f" }}
              disabled={isDownloading}
            >
              <DeleteOutlined />
            </Button>
          </Popconfirm>
        )}
      </div>
    </div>
  );
};

const AttachmentsList = ({ attachments, procedureId, onRefresh }) => (
  <div style={{ gap: "5px", ...styles.flexColumn }}>
    {attachments.map((attachment, index) => (
      <AttachmentItemList
        key={
          attachment.id || attachment.uuid || attachment.attachment_id || index
        }
        attachment={attachment}
        procedureId={procedureId}
        onRefresh={onRefresh}
      />
    ))}
  </div>
);

const NoAttachments = () => {
  return (
    <div style={{ ...styles.listItemContainer, justifyContent: "flex-start" }}>
      <div style={{ ...styles.iconMuted, ...styles.marginRight }}>
        <InfoCircleOutlined />
      </div>
      <h3 style={{ ...styles.text, color: "#aaa" }}>Sem anexos para exibir</h3>
    </div>
  );
};

export default AttachmentsModal;
